import { useState, useCallback } from "react";
import { type SelectionChangedArgs, SelectionChangedType } from "interfaces/onSelectionChanged";

type ItemSelectionOptions<T> = {
  onSelectionChange?: (selectedItems: T[]) => void;
  getItemId?: (item: T) => number;
};

export function useItemsSelection<T extends Record<string, any>>(options: ItemSelectionOptions<T> = {}) {
  const { onSelectionChange, getItemId = (item: T) => item.id as number } = options;

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  const updateSelection = useCallback(
    (ids: number[], items: T[]) => {
      setSelectedIds(ids);
      setSelectedItems(items);
      onSelectionChange?.(items);
    },
    [onSelectionChange],
  );

  const select = useCallback(
    (item: T | T[]) => {
      const items = Array.isArray(item) ? item : [item];
      const newSelectedIds = new Set([...selectedIds, ...items.map(getItemId)]);
      const newSelectedItems = [...selectedItems, ...items.filter((i) => !selectedIds.includes(getItemId(i)))];

      updateSelection([...newSelectedIds], newSelectedItems);
    },
    [selectedIds, selectedItems, getItemId, updateSelection],
  );

  const clearSelection = useCallback(() => {
    updateSelection([], []);
  }, [updateSelection]);

  const onSelectionChanged = useCallback(
    (args: SelectionChangedArgs<T>) => {
      if (args.type === SelectionChangedType.Cleared) {
        clearSelection();
        return;
      }

      if (args.type === SelectionChangedType.Added) {
        const newItems = args.items.filter((item) => !selectedIds.includes(getItemId(item)));
        if (newItems.length > 0) {
          const updatedItems = [...selectedItems, ...newItems];
          const updatedIds = updatedItems.map(getItemId);
          updateSelection(updatedIds, updatedItems);
        }
      } else {
        const idsToRemove = args.items.map(getItemId);
        const updatedItems = selectedItems.filter((item) => !idsToRemove.includes(getItemId(item)));
        const updatedIds = updatedItems.map(getItemId);
        updateSelection(updatedIds, updatedItems);
      }
    },
    [selectedIds, selectedItems, getItemId, clearSelection, updateSelection],
  );

  return {
    selectedIds,
    selectedItems,

    select,
    clearSelection,
    onSelectionChanged,
  };
}
